import { ctaDesc, ctaTitle } from "@/lib/seo";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { THAI_EMBASSIES_ABROAD, findThaiEmbassy } from "@/data/thai-embassies-abroad";
import { Building2, MapPin, Phone, Mail, Clock, FileCheck2 } from "lucide-react";
import { CountryDeepContent } from "@/components/CountryDeepContent";
import { isHagueSlug, normalizeCountrySlug } from "@/lib/country-hague";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/thai-embassy/$country")({
  loader: ({ params }) => {
    const e = findThaiEmbassy(params.country);
    if (!e) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const e = findThaiEmbassy(params.country);
    if (!e) return { meta: [{ title: ctaTitle("ไม่พบสถานทูต", "/thai-embassy/$country", "th")}] };
    const url = `${SITE}/thai-embassy/${e.slug}`;
    const title = `สถานทูตไทย ${e.city} ${e.flag} · พาสปอร์ต รับรอง เสร็จ ${e.processingDays} วัน | NYC`;
    const desc = `สถานเอกอัครราชทูตไทย ณ ${e.city} ${e.countryTh} — ที่อยู่ ${e.addressEn}, โทร ${e.tel}, เวลาทำการ ${e.hoursTh}. บริการ: ${e.services.slice(0,4).join(" · ")}. เขตอำนาจ: ${e.jurisdiction} — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(title, "/thai-embassy/$country", "th") }, { name: "description", content: ctaDesc(desc.slice(0, 300), "/thai-embassy/$country", "th")},
        { property: "og:title", content: ctaTitle(title, "/thai-embassy/$country", "th")},
        { property: "og:description", content: ctaDesc(desc.slice(0, 300), "/thai-embassy/$country", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "GovernmentOffice",
          "@id": `${url}#office`, name: `Royal Thai Embassy ${e.city}`,
          alternateName: `สถานทูตไทย ${e.city}`, url,
          address: { "@type": "PostalAddress", streetAddress: e.addressEn, addressLocality: e.city, addressCountry: e.countryEn },
          telephone: e.tel, email: e.email, openingHours: e.hoursTh,
          areaServed: e.jurisdiction, availableLanguage: e.languagesAccepted,
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "สถานทูตไทยในต่างประเทศ", item: `${SITE}/thai-embassy` },
            { "@type": "ListItem", position: 3, name: e.countryTh, item: url },
          ],
        }) },
      ],
    };
  },
  component: ThaiEmbassyCountryPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบสถานทูต</h1>
      <Link to="/thai-embassy" className="mt-4 inline-block text-gold underline">กลับไปยังรายการทั้งหมด</Link>
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  ),
});

function ThaiEmbassyCountryPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const e = findThaiEmbassy(slug)!;
  const others = THAI_EMBASSIES_ABROAD.filter((x) => x.slug !== slug && x.region === e.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/70">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5">/</span>
            <Link to="/thai-embassy" className="hover:text-gold">สถานทูตไทย</Link>
            <span className="px-1.5">/</span>
            <span className="text-white/90">{e.city}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Building2 className="h-3.5 w-3.5" /> Royal Thai Embassy · {e.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{e.flag}</span>
            สถานทูตไทย ณ {e.city}
          </h1>
          <p className="mt-4 max-w-3xl text-white/85 sm:text-lg">
            เขตอำนาจกงสุล: {e.jurisdiction}. {e.notes}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 sm:py-14">
        <section className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">ที่อยู่</div>
            <div className="mt-1 flex items-start gap-2 text-sm text-slate-800"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {e.addressEn}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">ติดต่อ</div>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-800"><Phone className="h-4 w-4 text-amber-600" /> {e.tel}</div>
            {e.emergencyTel && <div className="mt-1 flex items-center gap-2 text-sm text-rose-700"><Phone className="h-4 w-4" /> ฉุกเฉิน 24 ชม.: {e.emergencyTel}</div>}
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-800"><Mail className="h-4 w-4 text-amber-600" /> {e.email}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">เวลาทำการ</div>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-800"><Clock className="h-4 w-4 text-amber-600" /> {e.hoursTh}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">ระยะเวลา + ภาษา</div>
            <div className="mt-1 text-sm text-slate-800">เสร็จภายใน ~{e.processingDays} วันทำการ</div>
            <div className="mt-1 text-sm text-slate-800">ภาษา: {e.languagesAccepted.join(", ")}</div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">บริการที่ให้</h2>
          <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
            {e.services.map((s) => (
              <li key={s} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {s}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ค่าธรรมเนียม (โดยประมาณ)</h2>
          <table className="mt-4 w-full overflow-hidden rounded-xl border border-slate-200 text-sm">
            <thead className="bg-slate-100 text-left"><tr><th className="p-3">รายการ</th><th className="p-3">ค่าธรรมเนียม</th></tr></thead>
            <tbody>
              {e.fees.map((f) => (
                <tr key={f.service} className="border-t border-slate-200"><td className="p-3">{f.service}</td><td className="p-3 font-bold">{f.feeUsd ? `USD ${f.feeUsd}` : f.feeLocal}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-slate-500">* ราคาอ้างอิงกรมการกงสุล; โปรดตรวจสอบกับสถานทูตอีกครั้งก่อนยื่น</p>
        </section>

        <section className="mt-10 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep">
            NYC ช่วยเตรียมเอกสารต้นทางจากไทยก่อนยื่นสถานทูตที่ {e.city}
          </h2>
          <p className="mt-2 text-slate-700">
            เอกสารต้นทางไทย (สูติบัตร, ทะเบียนสมรส, หนังสือรับรองโสด, หนังสือมอบอำนาจ) ต้องผ่าน
            MFA/Apostille + แปลตามภาษาที่สถานทูตยอมรับ ({e.languagesAccepted.join(" / ")}) ก่อนส่งไป {e.city}
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <Link to="/mfa-legalisation" className="rounded-full bg-gold px-5 py-2 font-bold text-navy-deep">รับรอง MFA</Link>
            <Link to="/apostille" className="rounded-full border border-navy-deep px-5 py-2 font-bold text-navy-deep">Apostille</Link>
            <Link to="/translate" className="rounded-full border border-navy-deep px-5 py-2 font-bold text-navy-deep">แปลเอกสาร</Link>
            <a href="https://line.me/ti/p/@nycli" className="rounded-full bg-emerald-600 px-5 py-2 font-bold text-white">ปรึกษา LINE</a>
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-12 border-t border-slate-200 pt-8">
            <h2 className="font-display text-xl font-extrabold text-navy-deep">สถานทูตไทยในภูมิภาคเดียวกัน</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/thai-embassy/$country" params={{ country: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50">
                  <div className="text-2xl">{o.flag}</div>
                  <div>
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.countryTh}</div>
                    <div className="text-[11px] text-slate-500">{o.city}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <CountryDeepContent
        lang="th"
        service="thai-embassy"
        countryTh={e.countryTh}
        countryEn={e.countryEn}
        flag={e.flag}
        hague={isHagueSlug(normalizeCountrySlug(e.slug))}
        turnaround={`${e.processingDays} วันทำการ`}
        slug={normalizeCountrySlug(e.slug)}
      />
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  );
}
